import { useState } from "react";
import { format } from "date-fns";
import { LoaderCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AttachmentUploader } from "@/components/form/AttachmentUploader";
import { DateField } from "@/components/form/DateField";
import { FormField, FormSection, inputClass } from "@/components/form/FormField";
import { JenisSelect } from "@/components/form/JenisSelect";
import { SuccessDialog } from "@/components/form/SuccessDialog";
import { UploadOverlay } from "@/components/form/UploadOverlay";
import { useAttachments } from "@/hooks/useAttachments";
import { api, formatApiError } from "@/lib/api";
import { formatPhone } from "@/lib/format";

const PHONE_RE = /^(\+?62|0)8\d{7,12}$/;
const emptyForm = (user) => ({
  nama: user?.name || "", alamat: "", nomor_hp: formatPhone(user?.phone), tanggal: new Date(), jenis: "", detail: "",
});
const REQUIRED = {
  nama: "Nama pelapor wajib diisi",
  nomor_hp: "Nomor HP wajib diisi",
  alamat: "Alamat / nomor rumah wajib diisi",
  jenis: "Pilih jenis pengaduan",
  detail: "Detail pengaduan wajib diisi",
};
const validate = (form) => {
  const found = Object.fromEntries(Object.entries(REQUIRED).filter(([key]) => !String(form[key]).trim()));
  if (!found.nomor_hp && !PHONE_RE.test(form.nomor_hp.replace(/[\s-]/g, ""))) {
    found.nomor_hp = "Format nomor HP tidak valid (contoh: 081234567890)";
  }
  return found;
};

export const ComplaintForm = ({ user }) => {
  const [form, setForm] = useState(() => emptyForm(user));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);
  const attachments = useAttachments();

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const found = validate(form);
    if (Object.keys(found).length) {
      setErrors(found);
      return toast.error("Mohon lengkapi data yang wajib diisi");
    }
    if (attachments.uploading) return toast.warning("Tunggu hingga semua lampiran selesai diunggah");
    setSubmitting(true);
    try {
      const { data } = await api.post("/complaints", {
        nama: form.nama, alamat: form.alamat, nomor_hp: form.nomor_hp, jenis: form.jenis, detail: form.detail,
        tanggal: format(form.tanggal, "yyyy-MM-dd"), attachment_ids: attachments.readyIds,
      });
      setTicket(data.ticket);
      toast.success("Pengaduan Berhasil Dikirim!", { description: `Nomor tiket #${data.ticket}` });
    } catch (err) {
      toast.error(formatApiError(err, "Gagal mengirim pengaduan. Silakan coba lagi."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm(emptyForm(user));
    setErrors({});
    attachments.reset();
    setTicket(null);
  };

  return (
    <>
      <form
        onSubmit={handleSubmit}
        noValidate
        className="fade-up space-y-10 rounded-[28px] border border-slate-200/80 bg-white p-5 shadow-xl shadow-slate-900/[0.04] sm:p-8 lg:p-10"
        style={{ animationDelay: "120ms" }}
        data-testid="complaint-form"
      >
        <FormSection step="01" title="Data Pelapor" subtitle="Identitas dan alamat rumah Anda di Cluster Merak.">
          <div className="grid gap-5 md:grid-cols-2">
            <FormField id="nama" label="Nama Pelapor" required error={errors.nama}>
              <Input id="nama" value={form.nama} onChange={(e) => setField("nama", e.target.value)} placeholder="Nama lengkap Anda" maxLength={120} className={inputClass(errors.nama)} data-testid="input-nama-pelapor" />
            </FormField>
            <FormField id="nomor_hp" label="Nomor HP (WhatsApp)" required error={errors.nomor_hp} hint="Untuk notifikasi tanggapan & melacak tiket.">
              <Input id="nomor_hp" type="tel" inputMode="tel" value={form.nomor_hp} onChange={(e) => setField("nomor_hp", e.target.value)} placeholder="081234567890" maxLength={20} className={inputClass(errors.nomor_hp)} data-testid="input-nomor-hp" />
            </FormField>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            <FormField id="alamat" label="Alamat / Nomor Rumah" required error={errors.alamat}>
              <Input id="alamat" value={form.alamat} onChange={(e) => setField("alamat", e.target.value)} placeholder="Cluster Merak Blok X No. Y" maxLength={200} className={inputClass(errors.alamat)} data-testid="input-alamat" />
            </FormField>
            <FormField id="tanggal" label="Tanggal Pengaduan" hint="Otomatis terisi tanggal hari ini, dapat diubah bila perlu.">
              <DateField value={form.tanggal} onChange={(d) => setField("tanggal", d)} />
            </FormField>
          </div>
        </FormSection>

        <FormSection step="02" title="Isi Pengaduan" subtitle="Pilih kategori dan jelaskan masalahnya sejelas mungkin.">
          <FormField id="jenis" label="Jenis Pengaduan" required error={errors.jenis}>
            <JenisSelect value={form.jenis} onChange={(v) => setField("jenis", v)} invalid={!!errors.jenis} />
          </FormField>
          <FormField id="detail" label="Detail Pengaduan" required error={errors.detail}>
            <Textarea id="detail" rows={6} maxLength={5000} value={form.detail} onChange={(e) => setField("detail", e.target.value)} placeholder="Ceritakan apa yang terjadi, lokasi tepatnya, dan sejak kapan..." className={`rounded-xl border-slate-200 px-3.5 py-3 text-sm focus-visible:ring-2 focus-visible:ring-emerald-500/30 ${errors.detail ? "border-rose-400" : ""}`} data-testid="input-detail-pengaduan" />
            <p className="text-right text-xs text-slate-400">{form.detail.length}/5000</p>
          </FormField>
        </FormSection>

        <FormSection step="03" title="Lampiran Foto / Video" subtitle="Bukti visual membantu pengurus RT memahami masalah lebih cepat.">
          <AttachmentUploader items={attachments.items} onAdd={attachments.addFiles} onRemove={attachments.remove} />
        </FormSection>

        <div className="flex flex-col-reverse gap-4 border-t border-slate-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-slate-500">Kolom bertanda <span className="text-rose-500">*</span> wajib diisi.</p>
          <Button type="submit" disabled={submitting || attachments.uploading} className="h-12 rounded-xl bg-emerald-600 px-8 text-base font-semibold shadow-lg shadow-emerald-600/20 transition-transform hover:bg-emerald-700 active:scale-[0.98]" data-testid="submit-complaint-btn">
            {submitting ? <LoaderCircle className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            Kirim Pengaduan
          </Button>
        </div>
      </form>
      <UploadOverlay uploading={attachments.uploading} progress={attachments.progress} submitting={submitting} />
      <SuccessDialog ticket={ticket} onClose={handleReset} />
    </>
  );
};
